import { css } from '@/styled-system/css';

export const spokeContainer = css({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  height: '100%',
  width: '100%',
});

export const spokeContentContainer = css({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  width: '100%',
});

export const spokeHeader = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: '2x',
  paddingBottom: '2x',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderColor: 'hpxGrey400',
  bg: 'hpxWhite',
  flexShrink: 0,
});

export const headerLeft = css({
  display: 'flex',
  alignItems: 'center',
  gap: '2x',
});

export const headerRight = css({
  display: 'flex',
  alignItems: 'center',
  gap: '1x',
});

export const spokeTitle = css({
  fontSize: 'lg',
  fontWeight: 'bold',
  color: 'hpxBlue600',
  margin: 0,
});

export const spokeBody = css({
  flex: 1,
  paddingTop: '4x',
  paddingBottom: '4x',
  bg: 'hpxWhite',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  overflow: 'auto',
});

export const placeholderMap = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  maxWidth: '500px',
  height: '300px',
  bg: 'hpxGrey100',
  borderRadius: '8px',
  borderWidth: '2px',
  borderStyle: 'dashed',
  borderColor: 'hpxGrey400',
  marginBottom: '3x',
  gap: '2x',
});

export const mapIcon = css({
  fontSize: '4xl',
  opacity: 0.6,
});

export const placeholderTitle = css({
  fontSize: 'xl',
  fontWeight: 'bold',
  color: 'hpxBlue600',
  marginBottom: '1x',
});

export const placeholderSubtitle = css({
  fontSize: 'md',
  color: 'hpxGrey600',
  marginBottom: '2x',
});

export const addressInfo = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'xs',
  fontSize: 'sm',
  color: 'hpxGrey500',
});

export const featureList = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '2x',
  width: '100%',
  maxWidth: '400px',
});

export const featureListTitle = css({
  fontSize: 'lg',
  fontWeight: '600',
  color: 'hpxBlue600',
  textAlign: 'center',
  marginBottom: '1x',
});

export const featureItems = css({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  rowGap: '1x',
  columnGap: '2x',
  md: {
    gridTemplateColumns: '1fr',
  },
});

export const featureItem = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'xs',
  fontSize: 'sm',
  color: 'hpxGrey600',
  padding: 'xs',
  bg: 'hpxGrey100',
  borderRadius: '4px',
});

export const featureIcon = css({
  fontSize: 'md',
});

export const footerButtonGroup = css({
  display: 'flex',
  gap: '1x',
  flexWrap: 'wrap',
  width: '100%',
  maxWidth: '400px',
  '& > *': {
    flex: 1, // Equal width distribution like FullscreenOverlay "equal-width"
  },
});

// Map container - edge-to-edge with height constraint, square corners
// When data-expanded="true" (street view), fills available vertical space
export const mapContainer = css({
  width: '100%',
  height: '402px',
  maxHeight: '402px',
  position: 'relative',
  marginTop: '2x',
  md: {
    marginTop: 0,
  },
  // Override DynamicMap border-radius to square off corners
  '& > *': {
    borderRadius: '0',
  },
  // Also target the Google Maps container
  '& .gm-style': {
    borderTopLeftRadius: '0 !important',
    borderTopRightRadius: '0 !important',
    borderBottomLeftRadius: '0 !important',
    borderBottomRightRadius: '0 !important',
  },
  '&[data-expanded="true"]': {
    height: 'calc(100dvh - 150px)',
    maxHeight: 'none',
    md: {
      height: 'calc(100vh - 200px)',
    },
  },
});

// Wrapper for horizontally scrollable rows — adds a right-edge fade gradient
export const scrollFadeWrapper = css({
  position: 'relative',
  _after: {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: '40px',
    backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,1))',
    pointerEvents: 'none',
    zIndex: 'zIndexStep',
  },
});

// POI pills container - single row with horizontal scroll
export const poiContainer = css({
  display: 'flex',
  gap: '1x',
  flexWrap: 'nowrap', // Force single row
  overflowX: 'auto', // Enable horizontal scrolling
  paddingTop: '2x',
  paddingLeft: '2x',
  paddingRight: '5x',
  justifyContent: 'flex-start', // Align left for scrolling
  // Hide scrollbar for cleaner look
  scrollbarWidth: 'none', // Firefox
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome/Safari
  },
});

// Individual POI pill - handles both active and inactive states via data attribute
export const poiPill = css({
  borderRadius: '63px',
  color: 'hpxWhite',
  fontWeight: '700',
  fontSize: 'sm',
  paddingTop: '2px',
  paddingBottom: '2px',
  paddingLeft: '10.5px',
  paddingRight: '10.5px',
  height: '21px', // Exact height specified
  border: 'none',
  cursor: 'pointer',
  transitionProperty: 'background',
  transitionDuration: '0.2s',
  transitionTimingFunction: 'ease',
  display: 'flex',
  alignItems: 'center',

  // Default inactive state
  background: 'rgba(110, 110, 110, 0.60)',
  _hover: {
    background: 'rgba(110, 110, 110, 0.80)',
  },

  // Active state (when data-active="true")
  '&[data-active="true"]': {
    backgroundColor: 'hpxTeal500 !important', // Use !important to force override
    _hover: {
      backgroundColor: 'hpxTeal600 !important',
    },
  },
});

// Note: Active state is now handled via data-active attribute in poiPill above

// Commute section - 16px from pills, no border
export const commuteSection = css({
  marginTop: '2x',
  paddingLeft: '2x',
  paddingRight: '2x',
});

export const commuteLabel = css({
  fontSize: 'md',
  fontWeight: '700',
  color: 'hpxBlue500',
});

export const commuteInputContainer = css({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  marginTop: 'xs',
});

export const commuteInput = css({
  width: '100%',
  height: '36px',
  paddingLeft: '32px',
  paddingRight: '1x',
  paddingTop: '1x',
  paddingBottom: '1x',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'hpxGrey400',
  borderRadius: '4px',
  fontSize: 'md',
  color: 'hpxBlue500',
  background: 'hpxWhite',
  _placeholder: {
    color: 'hpxGrey500',
  },
  _focusVisible: {
    outline: 'none',
    borderColor: 'hpxTeal500',
    boxShadow: '0 0 0 1px hpxTeal500',
  },
});

export const commuteIcon = css({
  position: 'absolute',
  left: '1x', // 8px from left edge (internal padding)
  top: '50%',
  transform: 'translateY(-50%)',
  width: '16px',
  height: '16px',
  color: 'hpxTeal500',
  zIndex: 'zIndexStep',
  pointerEvents: 'none', // Prevent icon from interfering with input focus
});

export const viewToggle = css({
  position: 'absolute',
  top: '2x',
  left: '2x',
  zIndex: 'zIndexStepOver',
  display: 'flex',
  gap: '1x',
});

export const viewToggleButton = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  paddingTop: '8px',
  paddingBottom: '8px',
  paddingLeft: '13px',
  paddingRight: '13px',
  borderRadius: '8px',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: '#dfe3e3',
  bg: 'hpxWhite',
  color: 'hpxBlue500',
  fontSize: '13px',
  fontWeight: 'bold',
  letterSpacing: '0.3px',
  cursor: 'pointer',
  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
  transitionProperty: 'background, border-color',
  transitionDuration: '0.15s',

  _hover: {
    bg: 'hpxGrey100',
    borderColor: 'hpxTeal500',
  },
});

export const viewToggleIcon = css({
  width: '14px',
  height: '14px',
  flexShrink: 0,
});
